import { useCallback, useMemo, useState } from "react";

import type {
  FilterConfig,
  FilterMode,
  FilterState,
  FilterValue,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { buildWhereFromStates, countActiveFilters } from "#app/lib/filters/build-where";
import { useWeekStartsOn } from "#app/lib/utils/date-presets";
import { hasQuickFilterValue } from "#app/lib/filters/quick-values";
import { migrateStoredFilterState } from "#app/lib/filters/stored-filters";

const EMPTY_STATE: FilterState = { blocks: [], logic: "AND" };

/**
 * Generic filter state hook.
 *
 * Simple and advanced modes maintain SEPARATE FilterState objects, and BOTH
 * apply at once — `activeMode` is only which editor the panel shows, never a
 * query switch. `where` combines the two, `activeFilterCount` sums them, and
 * the per-mode counts feed the panel's tab badges so filters never "vanish"
 * into the tab you're not looking at.
 *
 * `initial` seeds the starting mode/state (mount-only, like a `useState`
 * initializer) — for surfaces that open straight into the advanced builder,
 * e.g. a widget's transient header filter popover, and for a saved view,
 * whose stored filters seed `simpleState`. A host that re-seeds on a new
 * subject remounts the hook (a `key`), the way the issue screens key their
 * filter provider on the view.
 */
export function useFilters(
  config: FilterConfig,
  initial?: { mode?: FilterMode; simpleState?: FilterState; advancedState?: FilterState },
): UseFiltersResult {
  const [activeMode, setActiveMode] = useState<FilterMode>(initial?.mode ?? "simple");
  // Seeded through the stored-shape migration: a saved view can carry field
  // shapes the panel no longer edits (see `migrateStoredFilterState`). Mount-
  // only, like the seed itself.
  const [simpleState, setSimpleState] = useState<FilterState>(() =>
    initial?.simpleState ? migrateStoredFilterState(initial.simpleState) : EMPTY_STATE,
  );
  const [advancedState, setAdvancedState] = useState<FilterState>(() =>
    initial?.advancedState ? migrateStoredFilterState(initial.advancedState) : EMPTY_STATE,
  );

  // ---------------------------------------------------------------------------
  // Quick filter values — a view onto simpleState.blocks[0]
  // ---------------------------------------------------------------------------

  const quickValues = useMemo(() => {
    if (!config.quickFilters || simpleState.blocks.length === 0) return {};
    const block0 = simpleState.blocks[0];
    const values: Record<string, unknown> = {};
    for (const qf of config.quickFilters) {
      const filter = block0?.filters.find((f) => f.field === qf.field);
      values[qf.field] = filter?.value ?? null;
    }
    return values;
  }, [simpleState, config.quickFilters]);

  const setQuickValue = useCallback(
    (field: string, value: unknown) => {
      const qf = config.quickFilters?.find((q) => q.field === field);
      if (!qf) return;

      setSimpleState((prev) => {
        // Simple mode keeps every quick filter in ONE block, so the edit below
        // always has a first block to land in: the existing one, copied so the
        // splice/push is not a mutation of the previous state, or a fresh one.
        const first = prev.blocks[0];
        const block: FilterState["blocks"][number] = first
          ? { ...first, filters: [...first.filters] }
          : { logic: "AND", filters: [] };
        const blocks = [block, ...prev.blocks.slice(1)];
        const idx = block.filters.findIndex((f) => f.field === field);

        if (!hasQuickFilterValue(value)) {
          if (idx >= 0) block.filters.splice(idx, 1);
        } else if (idx >= 0) {
          block.filters[idx] = { field, operator: qf.operator, value };
        } else {
          block.filters.push({ field, operator: qf.operator, value });
        }

        // Remove empty blocks
        const nextBlocks = blocks.filter((b) => b.filters.length > 0);
        return { ...prev, blocks: nextBlocks };
      });
    },
    [config.quickFilters]
  );

  // ---------------------------------------------------------------------------
  // Advanced filter mutations (operate on advancedState)
  // ---------------------------------------------------------------------------

  const addBlock = useCallback(() => {
    setAdvancedState((prev) => ({
      ...prev,
      blocks: [
        ...prev.blocks,
        { logic: "AND", filters: [{ field: "", operator: "eq", value: null }] },
      ],
    }));
  }, []);

  const removeBlock = useCallback((index: number) => {
    setAdvancedState((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((_, i) => i !== index),
    }));
  }, []);

  const setBlockLogic = useCallback(
    (index: number, logic: "AND" | "OR") => {
      setAdvancedState((prev) => ({
        ...prev,
        blocks: prev.blocks.map((b, i) =>
          i === index ? { ...b, logic } : b
        ),
      }));
    },
    []
  );

  const setMainLogic = useCallback((logic: "AND" | "OR") => {
    setAdvancedState((prev) => ({ ...prev, logic }));
  }, []);

  const addFilter = useCallback((blockIndex: number) => {
    setAdvancedState((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b, i) =>
        i === blockIndex
          ? {
              ...b,
              filters: [
                ...b.filters,
                { field: "", operator: "eq", value: null },
              ],
            }
          : b
      ),
    }));
  }, []);

  const updateFilter = useCallback(
    (
      blockIndex: number,
      filterIndex: number,
      update: Partial<FilterValue>
    ) => {
      setAdvancedState((prev) => ({
        ...prev,
        blocks: prev.blocks.map((b, bi) =>
          bi === blockIndex
            ? {
                ...b,
                filters: b.filters.map((f, fi) =>
                  fi === filterIndex ? { ...f, ...update } : f
                ),
              }
            : b
        ),
      }));
    },
    []
  );

  const removeFilter = useCallback(
    (blockIndex: number, filterIndex: number) => {
      setAdvancedState((prev) => {
        const nextBlocks = prev.blocks
          .map((b, bi) =>
            bi === blockIndex
              ? { ...b, filters: b.filters.filter((_, fi) => fi !== filterIndex) }
              : b
          )
          .filter((b) => b.filters.length > 0);

        return { ...prev, blocks: nextBlocks };
      });
    },
    []
  );

  // "Clear All" means all — both editors' filters.
  const clearAll = useCallback(() => {
    setSimpleState(EMPTY_STATE);
    setAdvancedState(EMPTY_STATE);
  }, []);

  // A saved filter is a snapshot of both editors, so applying one replaces
  // both — a merge would leave the previous question half-asked underneath.
  const applyState = useCallback(
    (nextSimple: FilterState, nextAdvanced: FilterState) => {
      setSimpleState(migrateStoredFilterState(nextSimple));
      setAdvancedState(migrateStoredFilterState(nextAdvanced));
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Derived values — both modes combined
  // ---------------------------------------------------------------------------

  const simpleFilterCount = useMemo(
    () => countActiveFilters(simpleState),
    [simpleState]
  );
  const advancedFilterCount = useMemo(
    () => countActiveFilters(advancedState),
    [advancedState]
  );
  const activeFilterCount = simpleFilterCount + advancedFilterCount;

  // A "last updated" preset resolves against the week start.
  const weekStartsOn = useWeekStartsOn();
  const where = useMemo(
    () => buildWhereFromStates(simpleState, advancedState),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the preset reads the week start from module state
    [simpleState, advancedState, weekStartsOn],
  );

  return useMemo(() => ({
    activeMode,
    setActiveMode,
    filterState: simpleState,
    advancedState,
    quickValues,
    setQuickValue,
    addBlock,
    removeBlock,
    setBlockLogic,
    setMainLogic,
    addFilter,
    updateFilter,
    removeFilter,
    clearAll,
    applyState,
    activeFilterCount,
    simpleFilterCount,
    advancedFilterCount,
    where,
  }), [
    activeMode, setActiveMode, simpleState, advancedState, quickValues,
    setQuickValue, addBlock, removeBlock, setBlockLogic, setMainLogic,
    addFilter, updateFilter, removeFilter, clearAll, applyState, activeFilterCount,
    simpleFilterCount, advancedFilterCount, where,
  ]);
}
